import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useSubscriptionStore } from '../stores/subscription'
import Home from '../views/Home.vue'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: { requiresAuth: false }
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('../views/About.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/Login.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('../views/Register.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/password-reset',
    name: 'ForgotPassword',
    component: () => import('../views/ForgotPassword.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/password-reset/verify',
    name: 'ResetPassword',
    component: () => import('../views/ResetPassword.vue'),
    meta: { requiresAuth: false }
  },
  // Redirect from old routes to admin routes
  {
    path: '/dashboard',
    redirect: '/admin/dashboard',
    meta: { requiresAuth: true }
  },
  {
    path: '/integrations',
    redirect: '/admin/integrations',
    meta: { requiresAuth: true }
  },
  {
    path: '/admin',
    redirect: '/admin/admin',
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/calendar',
    name: 'Calendar',
    component: () => import('../views/Calendar.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/subscription',
    name: 'Subscription',
    component: () => import('../views/Subscription.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/pricing',
    name: 'Pricing',
    component: () => import('../views/Pricing.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/documentation',
    name: 'Documentation',
    component: () => import('../views/Documentation.vue'),
    meta: { requiresAuth: false }
  },
  // Admin routes
  {
    path: '/admin/admin',
    name: 'AdminAdmin',
    component: () => import('../views/admin/Admin.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/account',
    name: 'AdminAccount',
    component: () => import('../views/admin/Account.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/my-account',
    name: 'MyAccount',
    component: () => import('../views/admin/Account.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/admin/dashboard',
    name: 'AdminDashboard',
    component: () => import('../views/admin/Dashboard.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/admin/planning',
    name: 'admin-planning',
    component: () => import('../views/admin/Planning.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/admin/planning/:id?',
    name: 'admin-planning-edit',
    component: () => import('../views/admin/Planning.vue'),
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/admin/integrations',
    name: 'AdminIntegrations',
    component: () => import('../views/admin/Integrations.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/maintenance',
    name: 'maintenance',
    component: () => import('../views/Maintenance.vue')
  },
  // Catch-all 404 route - must be the last route
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../views/NotFound.vue')
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const subscriptionStore = useSubscriptionStore()
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  
  console.log(`Route navigation to ${to.path}, authentication required: ${requiresAuth}, is authenticated: ${authStore.isAuthenticated}`)

  // If requires auth and not authenticated, redirect to login
  if (requiresAuth && !authStore.isAuthenticated) {
    console.log('Authentication required but user not authenticated, redirecting to login')
    next('/login')
    return
  } 

  // If authenticated but trying to access protected routes (except subscription and account pages)
  const allowedWithoutSubscription = ['/subscription', '/admin/account', '/my-account']
  if (requiresAuth && authStore.isAuthenticated && !allowedWithoutSubscription.includes(to.path)) {
    // Check if user has a valid paid subscription
    try {
      await subscriptionStore.fetchSubscription()
      const currentSubscription = subscriptionStore.currentPlan?.subscription
      
      // Check if user has a valid paid subscription
      const hasValidSubscription = currentSubscription && 
                                   currentSubscription.plan && 
                                   parseFloat(currentSubscription.plan.price) > 0 &&
                                   currentSubscription.plan.is_active
      
      if (!hasValidSubscription) {
        console.log('User does not have a valid paid subscription, redirecting to subscription page')
        next('/subscription')
        return
      }
    } catch (error) {
      console.error('Error checking subscription status:', error)
      // If we can't check subscription status, redirect to subscription page
      next('/subscription')
      return
    }
  }
  
  // If going to login/register while already authenticated, check subscription
  if ((to.path === '/login' || to.path === '/register') && authStore.isAuthenticated) {
    console.log('Already authenticated, checking subscription status...')
    try {
      await subscriptionStore.fetchSubscription()
      const currentSubscription = subscriptionStore.currentPlan?.subscription
      
      const hasValidSubscription = currentSubscription && 
                                   currentSubscription.plan && 
                                   parseFloat(currentSubscription.plan.price) > 0 &&
                                   currentSubscription.plan.is_active
      
      if (hasValidSubscription) {
        console.log('User has valid subscription, redirecting to admin dashboard')
        next('/admin/dashboard')
      } else {
        console.log('User needs subscription, redirecting to subscription page')
        next('/subscription')
      }
    } catch (error) {
      console.error('Error checking subscription status:', error)
      next('/subscription')
    }
    return
  }
  
  next()
})

export default router 